import { inventoryEndpoints } from '@/lib/api/endpoints'
import { inventoryApi } from '@/lib/api/http'
import type { PageResult } from '@/lib/api/common'

import type {
  Adjustment,
  AdjustmentQuery,
  CountLine,
  CountQuery,
  CreateAdjustmentBody,
  CreateCountBody,
  EnterCountLineBody,
  ReasonCode,
  ReasonCodeQuery,
  StockCount,
  CreateRecipeBody,
  CreateRecipeModifierBody,
  CreateStockItemBody,
  InventoryCategory,
  InventoryCategoryQuery,
  InventoryLocation,
  InventoryLocationQuery,
  InventoryUnit,
  InventoryUnitQuery,
  ItemStock,
  Recipe,
  RecipeModifier,
  RecipeModifierQuery,
  RecipeQuery,
  StockItem,
  StockItemQuery,
  StockLevel,
  StockLevelListQuery,
  StockLevelQuery,
  StockMovement,
  StockMovementQuery,
  UpdateRecipeBody,
  UpdateRecipeModifierBody,
  UpdateStockItemBody,
} from '../types'

/**
 * Stock items, one page of them.
 *
 * Every list on this service refuses a query param it does not declare, `''`
 * included. Undefined keys are dropped by axios, so an unset filter has to stay
 * `undefined` on the way here — never an empty string.
 */
export async function fetchStockItems(query: StockItemQuery): Promise<PageResult<StockItem>> {
  return await inventoryApi.get<PageResult<StockItem>>(inventoryEndpoints.items, {
    params: query,
  })
}

/**
 * Creates a stock item. Answers with nothing to read back — the list refetches.
 *
 * The service will not take the write without an `Idempotency-Key`, and replays
 * its first answer to a retry carrying the same key and body. So a request that
 * timed out after the server had already saved it comes back as the same success
 * on retry, rather than as a second item or a "SKU already exists".
 */
export async function createStockItem(
  body: CreateStockItemBody,
  idempotencyKey: string,
): Promise<void> {
  await inventoryApi.post<unknown>(inventoryEndpoints.items, body, {
    headers: { 'Idempotency-Key': idempotencyKey },
  })
}

/**
 * Changes a stock item. Answers with nothing to read back — the list refetches.
 *
 * No `Idempotency-Key`: the `version` already makes a replay harmless — a second
 * copy of the same change arrives stale and is refused with a conflict rather
 * than applied twice.
 */
export async function updateStockItem(id: string, body: UpdateStockItemBody): Promise<void> {
  await inventoryApi.patch<unknown>(`${inventoryEndpoints.items}/${id}`, body)
}

/** One stock item, with its totals. */
export async function fetchStockItem(id: string): Promise<StockItem> {
  return await inventoryApi.get<StockItem>(`${inventoryEndpoints.items}/${id}`)
}

/** One item's stock at every location it has a level at. */
export async function fetchItemStock(itemId: string): Promise<ItemStock> {
  return await inventoryApi.get<ItemStock>(`${inventoryEndpoints.stock}/${itemId}`)
}

export async function fetchRecipes(query: RecipeQuery): Promise<PageResult<Recipe>> {
  return await inventoryApi.get<PageResult<Recipe>>(inventoryEndpoints.recipes, {
    params: query,
  })
}

/** One recipe with its lines, at its current version. */
export async function fetchRecipe(id: string): Promise<Recipe> {
  return await inventoryApi.get<Recipe>(`${inventoryEndpoints.recipes}/${id}`)
}

/**
 * Adds a modifier to a recipe. Takes an `Idempotency-Key`, for the reason
 * `createStockItem` does. The recipe's version moves with it — a modifier is
 * part of its recipe — so a recipe read before this is stale after it.
 */
export async function createRecipeModifier(
  recipeId: string,
  body: CreateRecipeModifierBody,
  idempotencyKey: string,
): Promise<RecipeModifier> {
  return await inventoryApi.post<RecipeModifier>(
    `${inventoryEndpoints.recipes}/${recipeId}/modifiers`,
    body,
    { headers: { 'Idempotency-Key': idempotencyKey } },
  )
}

/** Changes a modifier on its recipe. Like adding one, it moves the recipe's version. */
export async function updateRecipeModifier(
  recipeId: string,
  modifierId: string,
  body: UpdateRecipeModifierBody,
): Promise<RecipeModifier> {
  return await inventoryApi.patch<RecipeModifier>(
    `${inventoryEndpoints.recipes}/${recipeId}/modifiers/${modifierId}`,
    body,
  )
}

/** Takes a modifier off its recipe for good, and answers with what it was. */
export async function deleteRecipeModifier(
  recipeId: string,
  modifierId: string,
): Promise<RecipeModifier> {
  return await inventoryApi.delete<RecipeModifier>(
    `${inventoryEndpoints.recipes}/${recipeId}/modifiers/${modifierId}`,
  )
}

/**
 * The modifiers of one recipe. They hang off a recipe — there is no list across
 * all of them — so a recipe that does not exist is a 404, not an empty page.
 */
export async function fetchRecipeModifiers(
  recipeId: string,
  query: RecipeModifierQuery,
): Promise<PageResult<RecipeModifier>> {
  return await inventoryApi.get<PageResult<RecipeModifier>>(
    `${inventoryEndpoints.recipes}/${recipeId}/modifiers`,
    { params: query },
  )
}

/**
 * A recipe for a COMPOSITE item that has none.
 *
 * Rare: every COMPOSITE item is created with a DRAFT recipe already, which is
 * filled in with `updateRecipe`. This is for an item made before drafts existed.
 * Takes an `Idempotency-Key`, for the same reason `createStockItem` does.
 */
export async function createRecipe(
  body: CreateRecipeBody,
  idempotencyKey: string,
): Promise<Recipe> {
  return await inventoryApi.post<Recipe>(inventoryEndpoints.recipes, body, {
    headers: { 'Idempotency-Key': idempotencyKey },
  })
}

/** Also how a DRAFT is filled in: its version, the lines, and status ACTIVE. */
export async function updateRecipe(id: string, body: UpdateRecipeBody): Promise<Recipe> {
  return await inventoryApi.patch<Recipe>(`${inventoryEndpoints.recipes}/${id}`, body)
}

/** Inventory's own categories — the ones a stock item can be filed under. */
export async function fetchInventoryCategories(
  query: InventoryCategoryQuery,
): Promise<PageResult<InventoryCategory>> {
  return await inventoryApi.get<PageResult<InventoryCategory>>(inventoryEndpoints.categories, {
    params: query,
  })
}

/** Units of measure — the system's first, then the business's own. */
export async function fetchInventoryUnits(
  query: InventoryUnitQuery,
): Promise<PageResult<InventoryUnit>> {
  return await inventoryApi.get<PageResult<InventoryUnit>>(inventoryEndpoints.units, {
    params: query,
  })
}

/** Branches and warehouses, as the inventory service mirrors them. */
export async function fetchInventoryLocations(
  query: InventoryLocationQuery,
): Promise<PageResult<InventoryLocation>> {
  return await inventoryApi.get<PageResult<InventoryLocation>>(inventoryEndpoints.locations, {
    params: query,
  })
}

/**
 * Stock levels, one row per item per location, most recently changed first.
 * Not the batch read below: no `itemIds`, so the service pages it.
 */
export async function fetchStockOnHand(
  query: StockLevelListQuery,
): Promise<PageResult<StockLevel>> {
  return await inventoryApi.get<PageResult<StockLevel>>(inventoryEndpoints.stock, {
    params: query,
  })
}

/** The stock ledger, newest first. */
export async function fetchStockMovements(
  query: StockMovementQuery,
): Promise<PageResult<StockMovement>> {
  return await inventoryApi.get<PageResult<StockMovement>>(inventoryEndpoints.movements, {
    params: query,
  })
}

/** Adjustments, wastage and opening balances, newest first, each with its lines. */
export async function fetchAdjustments(query: AdjustmentQuery): Promise<PageResult<Adjustment>> {
  return await inventoryApi.get<PageResult<Adjustment>>(inventoryEndpoints.adjustments, {
    params: query,
  })
}

/**
 * Saves a draft. Moves no stock — submitting it does. Takes an
 * `Idempotency-Key`, for the reason `createStockItem` does; the body's
 * `clientDocId` makes a second copy of the same document come back as the first.
 */
export async function createAdjustment(
  body: CreateAdjustmentBody,
  idempotencyKey: string,
): Promise<Adjustment> {
  return await inventoryApi.post<Adjustment>(inventoryEndpoints.adjustments, body, {
    headers: { 'Idempotency-Key': idempotencyKey },
  })
}

/** One step of a document's life — `submit`, `approve` or `cancel` — under its own key. */
async function adjustmentStep(
  id: string,
  step: 'submit' | 'approve' | 'cancel',
  idempotencyKey: string,
): Promise<Adjustment> {
  return await inventoryApi.post<Adjustment>(
    `${inventoryEndpoints.adjustments}/${id}/${step}`,
    undefined,
    { headers: { 'Idempotency-Key': idempotencyKey } },
  )
}

/**
 * Submits a draft: posts it, or leaves it pending approval when its value is over
 * the business's threshold. A line that would take on hand below zero refuses
 * the whole post with a 409, and the draft stays as it was.
 */
export function submitAdjustment(id: string, idempotencyKey: string): Promise<Adjustment> {
  return adjustmentStep(id, 'submit', idempotencyKey)
}

/** Approves one pending approval, which posts it. */
export function approveAdjustment(id: string, idempotencyKey: string): Promise<Adjustment> {
  return adjustmentStep(id, 'approve', idempotencyKey)
}

/** Cancels a draft or one pending approval. A posted one is final. */
export function cancelAdjustment(id: string, idempotencyKey: string): Promise<Adjustment> {
  return adjustmentStep(id, 'cancel', idempotencyKey)
}

/** Reasons: the shipped ones first, then the business's own. */
export async function fetchReasonCodes(query: ReasonCodeQuery): Promise<PageResult<ReasonCode>> {
  return await inventoryApi.get<PageResult<ReasonCode>>(inventoryEndpoints.reasonCodes, {
    params: query,
  })
}

/** Stock counts, newest first. Lines carry only what was frozen at post. */
export async function fetchCounts(query: CountQuery): Promise<PageResult<StockCount>> {
  return await inventoryApi.get<PageResult<StockCount>>(inventoryEndpoints.counts, {
    params: query,
  })
}

/** One count with its lines and entries; a blind one hides expected quantities until posted. */
export async function fetchCount(id: string): Promise<StockCount> {
  return await inventoryApi.get<StockCount>(`${inventoryEndpoints.counts}/${id}`)
}

/** Starts a count: one not-counted line per item. */
export async function createCount(
  body: CreateCountBody,
  idempotencyKey: string,
): Promise<StockCount> {
  return await inventoryApi.post<StockCount>(inventoryEndpoints.counts, body, {
    headers: { 'Idempotency-Key': idempotencyKey },
  })
}

/**
 * Counts one line. Its entries replace the ones before, so sending it again is
 * harmless — no `Idempotency-Key`. The answer's `warning` asks for a second look.
 */
export async function enterCountLine(
  countId: string,
  itemId: string,
  body: EnterCountLineBody,
): Promise<CountLine> {
  return await inventoryApi.put<CountLine>(
    `${inventoryEndpoints.counts}/${countId}/lines/${itemId}`,
    body,
  )
}

async function countStep(
  id: string,
  step: 'submit' | 'approve' | 'cancel',
  idempotencyKey: string,
): Promise<StockCount> {
  return await inventoryApi.post<StockCount>(
    `${inventoryEndpoints.counts}/${id}/${step}`,
    undefined,
    { headers: { 'Idempotency-Key': idempotencyKey } },
  )
}

/**
 * Submits a count: posts it — each counted line's variance as at the moment it
 * was counted — or leaves it pending approval over the threshold.
 */
export function submitCount(id: string, idempotencyKey: string): Promise<StockCount> {
  return countStep(id, 'submit', idempotencyKey)
}

export function approveCount(id: string, idempotencyKey: string): Promise<StockCount> {
  return countStep(id, 'approve', idempotencyKey)
}

export function cancelCount(id: string, idempotencyKey: string): Promise<StockCount> {
  return countStep(id, 'cancel', idempotencyKey)
}

/**
 * Several items' stock at one location, in one call.
 *
 * The ids travel comma-joined, which is the form the service documents. The
 * shared serializer would send an array as `itemIds=a&itemIds=b` instead — the
 * service happens to accept that too, but a filter that works by accident is one
 * a parser change breaks without a word.
 */
export async function fetchStockLevels({
  locationId,
  itemIds,
}: StockLevelQuery): Promise<PageResult<StockLevel>> {
  return await inventoryApi.get<PageResult<StockLevel>>(inventoryEndpoints.stock, {
    params: { locationId, itemIds: itemIds.join(',') },
  })
}
